import React, { useMemo, useState } from 'react';
import { BudgetExpenseOrIncome } from 'business/constants';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'components/Loading';
import { useParams } from 'react-router-dom';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { operationsToImputationGrid } from 'routes/BudgetModule/helpers/dataOperation';
import 'routes/BudgetModule/components/BudgetProposal/shared/Grids/_commonGrid.scss';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import { getResourceType } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import Imputations from './Imputations/Imputations';
import {
    getFormattedPivotedData,
    getResourceName,
} from './gridHelpers/helpers';
import Cadrage from './Cadrage/Cadrage';
import {
    useGetBudgetProposalSettings,
    useMutateBudgetProposalSettings,
} from './hooks/budgetProposalSettings';
import HttpErrorWrapper from './components/HttpErrorWrapper';
import { useGetResourceInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import NoDataState from 'ui/NoDataState/NoDataState';
import { useGetExpenseIncomeImputations } from './hooks/expenseIncomeImputations';

type Props = {
    budgetProposal: BudgetProposalT;
    resourceName: string;
    expenseOrIncome: BudgetExpenseOrIncome;
};

const StyContentWrapper = styled(Box, {
    name: 'StyContentWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '1rem',
    marginLeft: '2rem',
    marginRight: '2rem',
});

const StyGridView = styled(Box, {
    name: 'StyGridView',
})({
    width: '100%',
    // ---- START center rows vertically since we changed rowHeight ----
    '&&& .ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    '&&& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '16px',
        fontSize: '11px',
        '&.ag-right-aligned-cell': {
            justifyContent: 'flex-end',
        },
    },
    // ---- END center rows vertically since we changed rowHeight ----
});

const ExpenseIncome: React.FC<Props> = ({
    budgetProposal,
    resourceName,
    expenseOrIncome,
}) => {
    const { resourceSoftwareId = '' } = useParams();
    const [hasOrphanInscriptions, setHasOrphanInscriptions] =
        useState<boolean>(false);
    const { by, exercice } = budgetProposal;
    const { data: budgetProposals = [], isLoading: isBudgetProposalLoading } =
        useGetBudgetProposals();

    // -------- Gestionnaire/ Service Data --------

    const resourceType = getResourceType(by);

    /**
     * EMPTY SERVICE/GESTIONNAIRE case:
     * In case we have no service/gestionnaire selected and we are not in CIRIL case (where empty fake service/gestionnaire is created)
     * we don't want to filter on a fake org chart name, so we put the selectedResourceName as null.
     */
    const selectedResourceName = getResourceName(
        resourceName,
        resourceSoftwareId,
    );

    const { data: pivotedDataResponse, isLoading: isPivotedDataLoading } =
        useGetExpenseIncomeImputations(
            budgetProposal.id,
            resourceSoftwareId,
            expenseOrIncome,
        );

    // The queried data doesn't return the data in the same order.
    // If we don't sort the data here, useMemo detects a change in the cache and rerender
    // the components even though there might be none.
    const { pivotedData, columnsDetails } = getFormattedPivotedData(
        pivotedDataResponse,
        exercice,
    );

    const imputationsData = useMemo(
        () => operationsToImputationGrid(pivotedData, budgetProposal),
        [JSON.stringify(pivotedData), JSON.stringify(budgetProposal)],
    );

    // ---- Inscriptions Data ---

    const {
        data: inscriptionsDataResponse,
        isLoading: inscriptionsLoading,
        error,
        isError,
        cacheKey: inscriptionsQueryCacheKey,
    } = useGetResourceInscriptions({
        budgetBy: budgetProposal.by,
        budgetProposalIds: [budgetProposal.id],
        phase: budgetProposal.phase,
        expenseOrIncome,
        resourceSoftwareId,
        enabled: !!resourceSoftwareId,
    });

    // necessary to stabilize ref and avoid unmounting the detailcellrenderer of ImputationsGrid when updating its comment or amount
    const memoizedInscriptionsQueryCacheKey = useMemo(
        () => inscriptionsQueryCacheKey,
        [budgetProposal, expenseOrIncome, resourceSoftwareId],
    );

    const inscriptionsData = useMemo(
        // while waiting for the softwareId to be ready, we avoid sending undefined data.
        () => inscriptionsDataResponse || { data: [], columns: [] },
        [JSON.stringify(inscriptionsDataResponse)],
    );

    // -------- Settings --------
    const { settings, isSettingsLoading } = useGetBudgetProposalSettings(
        budgetProposal,
        resourceType,
        resourceSoftwareId,
    );

    const onSettingsEditMutation = useMutateBudgetProposalSettings(
        budgetProposal,
        resourceType,
        resourceSoftwareId,
    );

    // -------- Grid views --------
    const isLoading =
        inscriptionsLoading ||
        isPivotedDataLoading ||
        isBudgetProposalLoading ||
        isSettingsLoading;

    if (inscriptionsLoading || isBudgetProposalLoading) {
        return <Loading pastDelay />;
    }
    if (!budgetProposals) {
        return <NoDataState />;
    }
    return (
        <HttpErrorWrapper error={error} isError={isError}>
            <StyContentWrapper>
                <Cadrage
                    budgetProposal={budgetProposal}
                    inscriptionsData={inscriptionsData.data}
                    imputationsData={imputationsData}
                    resourceSoftwareId={resourceSoftwareId}
                    settings={settings}
                    onSettingsEdit={onSettingsEditMutation.mutate}
                    expenseOrIncome={expenseOrIncome}
                    hasOrphanInscriptions={hasOrphanInscriptions}
                />
                <div className="grid-wrapper">
                    <StyGridView>
                        <Imputations
                            budgetProposal={budgetProposal}
                            budgetProposals={budgetProposals}
                            imputationsData={imputationsData}
                            inscriptionsData={inscriptionsData}
                            settings={settings}
                            isLoading={isLoading}
                            expenseOrIncome={expenseOrIncome}
                            onSettingsEdit={onSettingsEditMutation.mutate}
                            resourceName={selectedResourceName}
                            resourceSoftwareId={resourceSoftwareId}
                            columnDetails={columnsDetails}
                            inscriptionsQueryCacheKey={
                                memoizedInscriptionsQueryCacheKey
                            }
                            setHasOrphanInscriptions={setHasOrphanInscriptions}
                        />
                    </StyGridView>
                </div>
            </StyContentWrapper>
        </HttpErrorWrapper>
    );
};

export default React.memo(ExpenseIncome);
